import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TourStyledProductTypeInfoModal, { TourStyledProductTypeInfoModalResult } from 'components/Tours/TourStyledProductTypeInfoModal'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { PRIVATE_TOUR_STYLED_HEADING, TOUR_STYLED_HEADING } from 'constants/tours'
import ModalContext from 'contexts/ModalContext'
import { EmptyArray } from 'lib/array/arrayUtils'
import { isTourProductTypeStyled } from 'lib/tours/tourUtils'
import { rem } from 'polished'
import React, { MouseEventHandler, useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'

interface TourStyleBenefit {
  image: App.Image
  title: string
  description: string
}

const TOUR_STYLES_BENEFITS: Record<App.Tours.V2ProductTypeStyled, Array<TourStyleBenefit>> = {
  deluxe_tour: [
    {
      image: { id: 'ogli9t02bmf7bpd3y01m' },
      title: 'Unmissable experiences',
      description: 'The best of every destination: curated activities and must-see highlights',
    },
    {
      image: { id: 'vohjh1k4mqb93gjbmnhb' },
      title: 'Premium accommodation',
      description: 'Carefully selected 4- and 4.5-star hotels for comfort and convenience throughout',
    },
  ],
  premium_tour: [
    {
      image: { id: 'pt7qf3vv7r2r1vbkper' },
      title: 'Dedicated agent',
      description: 'Personalised support with a dedicated Tours Concierge agent',
    },
    {
      image: { id: '4c00aigy1blnhl4zy4g' },
      title: 'Five-star accommodation',
      description: 'Elevated five-star hotels to ensure enhanced comfort and quality',
    },
  ],
  ultra_lux_tour: [
    {
      image: { id: 'inm4w1t2o6fg9nyac9i' },
      title: 'World-class stays',
      description: 'The world\'s most prestigious hotels, featuring unparalleled five-star properties and global icons',
    },
  ],
  signature_series_tour: [
    {
      image: { id: 'jsv70zstdiovyv92u68a' },
      title: 'Celebrity hosted',
      description: 'Ultra-exclusive journeys are designed and led by some of Australia’s most-loved personalities',
    },
    {
      image: { id: '3dlvhljdlpmxfjipck2p' },
      title: 'World-class stays',
      description: 'The world\'s most prestigious hotels, featuring unparalleled five-star properties and global icons',
    },
  ],
  custom_tour: [
    {
      image: { id: 'pt7qf3vv7r2r1vbkper' },
      title: 'Effortless Travel',
      description: 'Skip airport chaos with fast-tracked service at dedicated gates for stress-free travel between places not easily accessible via standard commercial routes',
    },
    {
      image: { id: '3dlvhljdlpmxfjipck2p' },
      title: 'Handpicked, Luxury Stays',
      description: 'Experience Europe at its finest with stays in the continent’s top 4- and 5-star hotels, with the option to elevate your journey even further with suite upgrades',
    },
  ],
  insider_series_tour: [
    {
      image: { id: 'tourinsideritinerarycvpimage' },
      title: 'Insider-Curated Itinerary',
      description: 'Explore a destination through an expert\'s eyes, with curated experiences & hidden gems they love most.',
    },
    {
      image: { id: 'tourexpertlycuratedcvpimage' },
      title: 'Flawless Execution',
      description: 'An insider-designed journey brought to life with the signature style and quality of Luxury Escapes.',
    },
  ],
}

const TOUR_STYLES_GROUP_BENEFIT: Record<App.Tours.V2ProductTypeStyled, TourStyleBenefit> = {
  deluxe_tour: {
    image: { id: 'iko1j3uoook3lxd3wn0r' },
    title: 'Max 24 travellers',
    description: 'Small-group journeys with a maximum of 24 travellers',
  },
  premium_tour: {
    image: { id: 'iko1j3uoook3lxd3wn0r' },
    title: 'Max 16 travellers',
    description: 'A more personal and immersive experience at every destination with a maximum of 16 travellers',
  },
  ultra_lux_tour: {
    image: { id: 'iko1j3uoook3lxd3wn0r' },
    title: 'Max 12 travellers',
    description: 'An intimate, exclusive journey with no more than 12 travellers, ensuring a personal and tailored experience',
  },
  signature_series_tour: {
    image: { id: 'io15a8zwv4o5agoksr' },
    title: 'One-off departure',
    description: 'Just one departure date, ensuring that every trip is a unique, once-in-a-lifetime experience',
  },
  custom_tour: {
    image: { id: 'iko1j3uoook3lxd3wn0r' },
    title: 'Flexibility, Designed to Fit You',
    description: 'Enhance your journey with a selection of curated experiences or enjoy the freedom to explore at your own pace with ample time to discover each destination your way',
  },
  insider_series_tour: {
    image: { id: 'toursmallgroupjourneycvpimage' },
    title: 'Small-Group Journeys',
    description: 'Travel in a small group for easier access to each destination.',
  },
}

const PRIVATE_TOUR_BENEFIT: TourStyleBenefit = {
  image: { id: 't5ctbm7gdm14t24ofo4' },
  title: 'Private tour',
  description: 'An intimate, exclusive journey with just you and your partner, ensuring a personal and tailored experience ',
}

const BenefitTile = styled.div`
  display: grid;
  gap: ${rem(20)};
  grid-template: "content image" auto / minmax(0, 1fr) ${rem(68)};

  ${mediaQueryUp.tablet} {
    grid-template: "image content" auto / ${rem(68)} minmax(0, 1fr);
  }
`

const BenefitContent = styled(VerticalSpacer)`
  grid-area: content;
`

const BenefitImage = styled(Image)`
  grid-area: image;
`

interface Props {
  offer: App.Tours.TourV2Offer
  onCheckAvailability: () => void
}

function TourStyleKeyBenefitsSection({ offer, onCheckAvailability }: Props) {
  const showModal = useContext(ModalContext)
  const openTourStyleInfoModal = useCallback<MouseEventHandler<HTMLButtonElement>>(async(event) => {
    event.preventDefault()
    if (!isTourProductTypeStyled(offer.productType)) return
    const result = await showModal<TourStyledProductTypeInfoModalResult>(<TourStyledProductTypeInfoModal
      primaryActionText="Check availability"
      productType={offer.productType}
    />)
    if (result === 'primary-action') {
      onCheckAvailability()
    }
  }, [onCheckAvailability, showModal, offer])

  const [styleHeading, styleBenefits] = useMemo<[styleHeading: string, styleBenefits: Array<TourStyleBenefit>]>(() => {
    if (!isTourProductTypeStyled(offer.productType)) return ['', EmptyArray satisfies Array<TourStyleBenefit>] as const
    const heading = offer.privateJourney ? PRIVATE_TOUR_STYLED_HEADING[offer.productType] : TOUR_STYLED_HEADING[offer.productType]
    const benefits = [...TOUR_STYLES_BENEFITS[offer.productType]]
    if (offer.privateJourney) benefits.push(PRIVATE_TOUR_BENEFIT)
    else benefits.push(TOUR_STYLES_GROUP_BENEFIT[offer.productType])
    return [heading, benefits] as const
  }, [offer])

  return <VerticalSpacer gap={24}>
    <HeadingTextBlock
      variant="heading4"
      endIcon={offer.productType !== 'custom_tour' && <Clickable onClick={openTourStyleInfoModal}><LineInfoCircleIcon /></Clickable>}
    >
      {styleHeading}
    </HeadingTextBlock>
    <Group
      direction="vertical"
      gap={20}
      tabletGap={24}
      tabletDirection="horizontal"
      tabletHorizontalAlign="stretch"
    >
      {styleBenefits.map((benefit, index) => <BenefitTile key={index}>
        <BenefitContent gap={4}>
          <Heading variant="heading6" colour="neutral-one">{benefit.title}</Heading>
          <BodyText variant="medium" colour="neutral-two">{benefit.description}</BodyText>
        </BenefitContent>
        <BenefitImage image={benefit.image} width={68} aspectRatio="1" dpr={2} />
      </BenefitTile>)}
    </Group>
  </VerticalSpacer>
}

export default TourStyleKeyBenefitsSection
